import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Badge, DemoBadge, DemoNotice, Empty, ErrorMsg, Loading, PageHead, Sq, StudioLogo, Unknown } from '../components/ui';
import { api, errorText } from '../lib/api';
import { activeFilterCount, apiQuery, demoMode, MULTI_KEYS, truthy, type MultiKey } from '../lib/filters';
import { COMPLETENESS_LABEL, countryName, DEPTH_LABEL, INTEREST_LABEL, PRICING_LABEL } from '../lib/format';
import { useApi, useSession } from '../lib/session';
import type { Facets, MapData, StudioRow } from '../lib/types';
import { AddStudioDialog, DiscoveryDialog, ImportDialog } from './competitors/Dialogs';

// Leaflet is loaded only when the map view opens.
const StudioMap = lazy(() => import('../components/StudioMap'));

type Option = { value: string; label: string; count?: number };

function MultiFilter({ label, k, options, sp, setParam }: { label: string; k: MultiKey; options: Option[]; sp: URLSearchParams; setParam: (k: MultiKey, v: string[]) => void }) {
  const selected = sp.getAll(k);
  return (
    <details className="filter" data-filter={k}>
      <summary className={selected.length ? 'on' : ''}>
        {label}
        {selected.length > 0 && <span className="num"> · {selected.length}</span>}
      </summary>
      <div className="filter-pop" role="group" aria-label={label}>
        {options.length === 0 && <p className="muted small">No values yet.</p>}
        {options.map((o) => (
          <label key={o.value} className="check">
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={(e) => setParam(k, e.target.checked ? [...selected, o.value] : selected.filter((x) => x !== o.value))}
            />{' '}
            {o.label}
            {o.count !== undefined && <span className="muted num"> {o.count}</span>}
          </label>
        ))}
        {selected.length > 0 && (
          <button type="button" className="link small" onClick={() => setParam(k, [])}>
            clear
          </button>
        )}
      </div>
    </details>
  );
}

function PricingCell({ v }: { v: string }) {
  if (v === 'not_checked') return <Unknown>not checked</Unknown>;
  return <span>{PRICING_LABEL[v] ?? v}</span>;
}

export function Competitors() {
  const { settings } = useSession();
  const [sp, setSp] = useSearchParams();
  const view = sp.get('view') === 'map' ? 'map' : 'list';
  const query = apiQuery(sp).toString();
  const list = useApi<{ total: number; items: StudioRow[] }>(`/api/studios${query ? `?${query}` : ''}`);
  const map = useApi<MapData>(`/api/studios/map${query ? `?${query}` : ''}`);
  const facets = useApi<Facets>('/api/studios/facets');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<'add' | 'import' | 'discovery' | null>(null);
  const [qInput, setQInput] = useState(sp.get('q') ?? '');
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkErr, setBulkErr] = useState('');
  const [bulkInterest, setBulkInterest] = useState('');
  const [bulkTags, setBulkTags] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  // Selection belongs to one result set.
  useEffect(() => setSelected(new Set()), [query]);

  // Search box -> URL, debounced.
  useEffect(() => {
    const t = setTimeout(() => {
      const cur = sp.get('q') ?? '';
      if (qInput.trim() !== cur) {
        const next = new URLSearchParams(sp);
        if (qInput.trim()) next.set('q', qInput.trim());
        else next.delete('q');
        setSp(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  // Close open filter popovers on outside click; registered once (M-090).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const root = filtersRef.current;
      if (!root) return;
      root.querySelectorAll('details.filter[open]').forEach((d) => {
        if (!d.contains(e.target as Node)) d.removeAttribute('open');
      });
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function update(mut: (n: URLSearchParams) => void) {
    const next = new URLSearchParams(sp);
    mut(next);
    setSp(next);
  }
  const setParam = (k: MultiKey, values: string[]) =>
    update((n) => {
      n.delete(k);
      values.forEach((v) => n.append(k, v));
    });
  const setOne = (k: string, v: string | null) => update((n) => (v ? n.set(k, v) : n.delete(k)));

  const f = facets.data;
  const v = settings?.values;
  const selectedCountries = sp.getAll('country');
  const options: Record<MultiKey, Option[]> = useMemo(
    () => ({
      country: (f?.countries ?? [])
        .filter((c) => c.code)
        .map((c) => ({ value: c.code!, label: `${countryName(c.code)} (${c.code})`, count: c.count }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      city: (f?.cities ?? [])
        .filter((c) => !selectedCountries.length || (c.country && selectedCountries.includes(c.country)))
        .map((c) => ({ value: c.city, label: `${c.city}${c.country ? `, ${c.country}` : ''}`, count: c.count })),
      team_size: [...(v?.team_size_bands ?? []).map((b) => ({ value: b, label: b })), { value: 'unknown', label: 'unknown' }],
      service: (f?.services ?? []).map((s) => ({ value: s, label: s })),
      tag: (f?.tags ?? []).map((s) => ({ value: s, label: s })),
      interest: Object.entries(INTEREST_LABEL).map(([value, label]) => ({ value, label })),
      depth: Object.entries(DEPTH_LABEL).map(([value, label]) => ({ value, label })),
      completeness: Object.entries(COMPLETENESS_LABEL).map(([value, label]) => ({ value, label })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [f, v, selectedCountries.join('|')],
  );
  const LABELS: Record<MultiKey, string> = {
    country: 'Country',
    city: 'City',
    team_size: 'Team size',
    service: 'Service',
    tag: 'Tag',
    interest: 'Interest',
    depth: 'Research depth',
    completeness: 'Completeness',
  };

  const items = list.data?.items ?? [];
  const otherOffices = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of map.data?.points ?? []) if (!p.is_hq) m.set(p.studio_id, (m.get(p.studio_id) ?? 0) + 1);
    return m;
  }, [map.data]);
  const anyDemo = items.some((s) => s.is_demo);
  const shown = list.data?.total;
  const mapped = map.data ? map.data.total - map.data.unmapped : undefined;
  const nFilters = activeFilterCount(sp);
  const allChecked = items.length > 0 && items.every((s) => selected.has(s.id));

  async function bulk(body: { interest?: string; add_tags?: string[]; remove_tags?: string[] }) {
    setBulkBusy(true);
    setBulkErr('');
    setBulkMsg('');
    try {
      const r = await api<{ updated: number }>('/api/studios/bulk', { method: 'POST', body: { ids: [...selected], ...body } });
      setBulkMsg(`${r.updated} ${r.updated === 1 ? 'studio' : 'studios'} updated.`);
      list.reload();
      map.reload();
      facets.reload();
    } catch (e) {
      setBulkErr(errorText(e));
    } finally {
      setBulkBusy(false);
    }
  }
  async function bulkWatch(watched: boolean) {
    setBulkBusy(true);
    setBulkErr('');
    setBulkMsg('');
    try {
      const r = await api<{ changed: number; unchanged: number }>('/api/watchlist/bulk', { body: { studio_ids: [...selected], watched } });
      setBulkMsg(
        `${r.changed} ${r.changed === 1 ? 'studio' : 'studios'} ${watched ? 'added to' : 'removed from'} the watch list` +
          (r.unchanged ? ` (${r.unchanged} already ${watched ? 'on it' : 'off it'})` : '') +
          '.',
      );
      list.reload();
    } catch (e) {
      setBulkErr(errorText(e));
    } finally {
      setBulkBusy(false);
    }
  }
  const tagList = bulkTags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <div className="page wide">
      <PageHead title="Competitors" meta="Light directory">
        <button type="button" className="btn primary" onClick={() => setDialog('add')}>
          Add studio
        </button>
        <button type="button" className="btn" onClick={() => setDialog('import')}>
          Import CSV
        </button>
        <button type="button" className="btn" onClick={() => setDialog('discovery')}>
          New discovery
        </button>
        <a className="btn" href={`/api/studios/export.csv${query ? `?${query}` : ''}`} download>
          Export CSV
        </a>
        <Link className="btn ghost" to="/competitors/merge">
          Merge suggestions
        </Link>
      </PageHead>

      <div className="filters" ref={filtersRef}>
        <label className="search">
          <span className="sr-only">Search name or domain</span>
          <input type="search" placeholder="Search name or domain" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </label>
        {MULTI_KEYS.map((k) => (
          <MultiFilter key={k} k={k} label={LABELS[k]} options={options[k]} sp={sp} setParam={setParam} />
        ))}
        <label className="select-filter">
          <span>Location</span>
          <select value={sp.get('location') ?? ''} onChange={(e) => setOne('location', e.target.value || null)}>
            <option value="">any</option>
            <option value="mapped">on the map</option>
            <option value="unmapped">list only</option>
          </select>
        </label>
        <label className="select-filter">
          <span>Demo</span>
          <select value={demoMode(sp)} onChange={(e) => setOne('demo', e.target.value === 'include' ? null : e.target.value)}>
            <option value="include">include</option>
            <option value="hide">hide</option>
            <option value="only">only demo</option>
          </select>
        </label>
        <label className="check inline">
          <input type="checkbox" checked={truthy(sp.get('include_dismissed'))} onChange={(e) => setOne('include_dismissed', e.target.checked ? '1' : null)} /> Show dismissed
        </label>
        {sp.get('batch_id') && (
          <span className="chip">
            Batch {sp.get('batch_id')!.slice(0, 8)}
            <button type="button" className="link small" onClick={() => setOne('batch_id', null)} aria-label="Clear batch filter">
              ×
            </button>
          </span>
        )}
        {nFilters > 0 && (
          <button
            type="button"
            className="link small"
            onClick={() => {
              setQInput('');
              setSp(new URLSearchParams(view === 'map' ? { view: 'map' } : {}));
            }}
          >
            Clear all filters
          </button>
        )}
      </div>

      <div className="coverage">
        <p className="coverage-line" data-testid="coverage">
          <span className="num" data-testid="shown">{shown ?? '…'}</span> studios shown · <span className="num" data-testid="mapped">{mapped ?? '…'}</span> on the
          map · <span className="num" data-testid="listonly">{map.data?.unmapped ?? '…'}</span> list-only (no geocoded city)
        </p>
        <div className="view-switch" role="tablist" aria-label="View">
          <button type="button" role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'tab active' : 'tab'} onClick={() => setOne('view', null)}>
            List
          </button>
          <button type="button" role="tab" aria-selected={view === 'map'} className={view === 'map' ? 'tab active' : 'tab'} onClick={() => setOne('view', 'map')}>
            Map
          </button>
        </div>
      </div>
      <p className="muted small census">
        <Sq kind="outline" /> This directory holds what has been entered, imported or discovered. It is not a complete census of any country.
      </p>
      {anyDemo && <DemoNotice />}
      <ErrorMsg>{list.error || map.error}</ErrorMsg>

      {view === 'list' && selected.size > 0 && (
        <div className="bulkbar" role="region" aria-label="Bulk actions">
          <strong className="num">{selected.size} selected</strong>
          <label className="select-filter">
            <span>Interest</span>
            <select value={bulkInterest} onChange={(e) => setBulkInterest(e.target.value)}>
              <option value="">choose…</option>
              {Object.entries(INTEREST_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {k === 'dismissed' ? 'Dismiss' : l}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn small" disabled={!bulkInterest || bulkBusy} onClick={() => void bulk({ interest: bulkInterest })}>
            Set interest
          </button>
          <label className="select-filter">
            <span>Tags</span>
            <input value={bulkTags} onChange={(e) => setBulkTags(e.target.value)} placeholder="tag, tag" />
          </label>
          <button type="button" className="btn small" disabled={!tagList.length || bulkBusy} onClick={() => void bulk({ add_tags: tagList })}>
            Add tags
          </button>
          <button type="button" className="btn small" disabled={!tagList.length || bulkBusy} onClick={() => void bulk({ remove_tags: tagList })}>
            Remove tags
          </button>
          <button type="button" className="btn small" disabled={bulkBusy} onClick={() => void bulkWatch(true)}>
            Add to watch list
          </button>
          <button type="button" className="btn small" disabled={bulkBusy} onClick={() => void bulkWatch(false)}>
            Remove from watch list
          </button>
          <button type="button" className="link small" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}
      {bulkMsg && <p className="msg ok small">{bulkMsg}</p>}
      <ErrorMsg>{bulkErr}</ErrorMsg>

      {view === 'map' ? (
        <>
          <Suspense fallback={<Loading what="Loading the map" />}>
          <StudioMap
            points={map.data?.points ?? []}
            tileUrl={v?.map.tile_url ?? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'}
            tileAttribution={v?.map.tile_attribution ?? '© OpenStreetMap contributors'}
            geoAttribution={map.data?.attribution ?? settings?.geo_attribution ?? 'GeoNames'}
          />
          </Suspense>
          <p className="muted small">
            Markers sit on city centroids from GeoNames, not on street addresses. {map.data?.attribution}.{' '}
            {map.data && map.data.unmapped > 0 && (
              <Link to={`/competitors?${(() => {
                const n = new URLSearchParams(sp);
                n.delete('view');
                n.set('location', 'unmapped');
                return n.toString();
              })()}`}>
                See the {map.data.unmapped} list-only {map.data.unmapped === 1 ? 'studio' : 'studios'}
              </Link>
            )}
          </p>
        </>
      ) : list.loading && !list.data ? (
        <Loading what="Loading studios" />
      ) : items.length === 0 ? (
        <Empty>{nFilters ? 'No studio matches these filters.' : 'The directory is empty. Add a studio, import a CSV or start a discovery.'}</Empty>
      ) : (
        <div className="table-wrap">
          {shown !== undefined && shown > items.length && (
            <p className="msg warn small">
              Showing the first {items.length} of {shown}. Narrow the filters, or export CSV for the full set.
            </p>
          )}
          <table className="table studios">
            <thead>
              <tr>
                <th className="cb">
                  <input
                    type="checkbox"
                    aria-label="Select all shown"
                    checked={allChecked}
                    onChange={(e) => setSelected(e.target.checked ? new Set(items.map((s) => s.id)) : new Set())}
                  />
                </th>
                <th>Studio</th>
                <th>HQ</th>
                <th>Team</th>
                <th>Services</th>
                <th>Interest</th>
                <th>Depth</th>
                <th>Completeness</th>
                <th>Pricing evidence</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const others = otherOffices.get(s.id) ?? 0;
                return (
                  <tr key={s.id} className={selected.has(s.id) ? 'selected' : ''}>
                    <td className="cb">
                      <input
                        type="checkbox"
                        aria-label={`Select ${s.name}`}
                        checked={selected.has(s.id)}
                        onChange={(e) => {
                          const n = new Set(selected);
                          if (e.target.checked) n.add(s.id);
                          else n.delete(s.id);
                          setSelected(n);
                        }}
                      />
                    </td>
                    <td className="name-cell" data-label="Studio">
                      <StudioLogo src={s.logo_url} />
                      <Link to={`/competitors/${s.id}`} className="studio-name">
                        {s.name}
                      </Link>{' '}
                      {s.is_demo && <DemoBadge />}
                      {s.watchlist && <Badge tone="deep" title="On the watch list">Watching</Badge>}
                      <div className="muted small">{s.domain ?? <Unknown>no website</Unknown>}</div>
                    </td>
                    <td data-label="HQ">
                      {s.city || s.country ? (
                        <>
                          {s.city ?? <Unknown>city unknown</Unknown>}
                          {s.country && <span className="muted">, {s.country}</span>}
                        </>
                      ) : (
                        <Unknown />
                      )}
                      {!s.mapped && <div className="small muted">not on map</div>}
                      {others > 0 && (
                        <div className="small muted" title="other offices with a geocoded city">
                          +{others} other {others === 1 ? 'office' : 'offices'}
                        </div>
                      )}
                    </td>
                    <td data-label="Team" className="num">
                      {s.team_size_band ?? <Unknown />}
                    </td>
                    <td data-label="Services" className="services-cell">
                      {s.services.length ? (
                        <>
                          {s.services.slice(0, 3).join(', ')}
                          {s.services.length > 3 && <span className="muted"> +{s.services.length - 3}</span>}
                        </>
                      ) : (
                        <Unknown />
                      )}
                    </td>
                    <td data-label="Interest">
                      <span className={`interest i-${s.interest}`}>{INTEREST_LABEL[s.interest]}</span>
                    </td>
                    <td data-label="Depth">{s.research_depth === 'deep' ? <Badge tone="deep">Deep</Badge> : <span className="muted">Light</span>}</td>
                    <td data-label="Completeness">
                      <span className={`compl c-${s.completeness}`}>
                        <Sq kind={s.completeness === 'complete' ? 'filled' : 'outline'} /> {COMPLETENESS_LABEL[s.completeness]}
                      </span>
                    </td>
                    <td data-label="Pricing">
                      <PricingCell v={s.pricing_evidence} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog === 'add' && <AddStudioDialog onClose={() => setDialog(null)} />}
      {dialog === 'import' && (
        <ImportDialog
          onClose={() => setDialog(null)}
          onDone={() => {
            list.reload();
            map.reload();
            facets.reload();
          }}
        />
      )}
      {dialog === 'discovery' && <DiscoveryDialog onClose={() => setDialog(null)} />}
    </div>
  );
}
